import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import type { FuseHit } from '@kanda-libs/ks-design-library';
import type { Company, Job } from '@kanda-libs/ks-frontend-services';

import { SEARCH_OPTIONS } from 'components/job/JobTable/constants';
import type { JobWithCompanyAndCredit } from 'components/job/JobTable/types';
import type { RefreshAllHook } from 'hooks/useRefreshAll';
import useSearchQuery from 'hooks/useSearchQuery';
import { selectors } from 'store';
import companySelectors from 'store/selectors/company';
import jobSelectors from 'store/selectors/job';

export interface CompanyHook {
  company?: Company;
  showRateRequest: boolean;
  jobs: JobWithCompanyAndCredit[];
  isSubmitting?: boolean;
  isLoading?: boolean;
  refreshAll: RefreshAllHook;
  hits: FuseHit<Job>[];
  query: string;
  setQuery: (query: string) => void;
}

export default function useCompany(): CompanyHook {
  const company = useSelector(selectors.company.getEntity);
  const companyIsLoading = useSelector(selectors.infoEntity.getIsLoading);

  const enrolment = useSelector(selectors.enrolment.getEntity);
  const enrolmentIsLoading = useSelector(selectors.enrolment.getIsLoading);

  const isLoading = useMemo(
    () => companyIsLoading || enrolmentIsLoading,
    [companyIsLoading, enrolmentIsLoading],
  );

  const filteredJobs = useSelector(companySelectors.getCompanyFilteredJobs);

  const searchQuery = useSearchQuery(filteredJobs, '', SEARCH_OPTIONS);

  const showRateRequest = useMemo(() => {
    if (isLoading) return false;
    const toAdd = enrolment?.rate_request.to_add || [];
    const toRemove = enrolment?.rate_request.to_remove || [];
    return toAdd.length > 0 || toRemove.length > 0;
  }, [isLoading, enrolment]);

  return {
    company,
    showRateRequest,
    isLoading,
    refreshAll: () => {},
    jobs: filteredJobs,
    ...searchQuery,
  };
}
